import React, { useEffect, useMemo, useRef, useState } from "react";
import { evaluateExpression } from "../../engine/evaluateBindings";

const CANVAS_WIDTH = 1000;
const CANVAS_HEIGHT = 700;

function normalizePoints(points = []) {
  return points.map((p, index) => ({
    id: p.id || `pt_${index}_${Math.random().toString(36).slice(2, 8)}`,
    x: p.x ?? 0,
    y: p.y ?? 0,
  }));
}


function createPoint(x = 0, y = 0) {
  return {
    id: `pt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    x,
    y,
  };
}

function snapValue(value, step) {
  if (!step || step <= 0) return value;
  return Math.round(value / step) * step;
}

export default function BoardSketchCanvas2D({
  profile,
  parameters = {},
  onChangeProfile,
  selectedPointId,
  onSelectPoint,
}) {
  const svgRef = useRef(null);

  const safeProfile = profile || {
    type: "polygon",
    points: [
      createPoint(0, 0),
      createPoint(600, 0),
      createPoint(600, 720),
      createPoint(0, 720),
    ],
    cutouts: [],
  };

  const points = useMemo(
    () => normalizePoints(safeProfile.points || []),
    [safeProfile.points]
  );

  const [tool, setTool] = useState("select"); // select | add | pan
  const [snapStep, setSnapStep] = useState(10);
  const [zoom, setZoom] = useState(0.6);
  const [pan, setPan] = useState({ x: 120, y: 560 });

  const [draggingPointId, setDraggingPointId] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const [lastPointer, setLastPointer] = useState(null);

  const gridMinor = 10;
  const gridMajor = 50;

  function patchPoints(nextPoints) {
    onChangeProfile?.({
      ...safeProfile,
      type: "polygon",
      points: nextPoints,
    });
  }

  function pointToNumber(pt) {
    return {
      x: evaluateExpression(pt.x, parameters, 0),
      y: evaluateExpression(pt.y, parameters, 0),
    };
  }

  function worldToScreen(pt) {
    const n = pointToNumber(pt);

    return {
      x: pan.x + n.x * zoom,
      y: pan.y - n.y * zoom,
    };
  }

  function screenToWorld(x, y) {
    return {
      x: snapValue((x - pan.x) / zoom, snapStep),
      y: snapValue((pan.y - y) / zoom, snapStep),
    };
  }

  function getSvgPoint(e) {
    if (!svgRef.current) return null;
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }

  function handleBackgroundClick(e) {
    if (tool !== "add") return;
    if (!svgRef.current) return;

    const p = getSvgPoint(e);
    if (!p) return;

    const world = screenToWorld(p.x, p.y);
    const next = [...points, createPoint(world.x, world.y)];
    patchPoints(next);
    onSelectPoint?.(next[next.length - 1].id);
  }

  function handlePointPointerDown(e, pointId) {
    e.stopPropagation();

    if (tool !== "select") {
      onSelectPoint?.(pointId);
      return;
    }

    setDraggingPointId(pointId);
    onSelectPoint?.(pointId);
  }

  function handlePointerDownBackground(e) {
    const p = getSvgPoint(e);
    setLastPointer(p);

    if (tool === "pan") {
      setIsPanning(true);
      return;
    }

    if (tool === "select") {
      onSelectPoint?.(null);
    }
  }

  function handlePointerMove(e) {
    const p = getSvgPoint(e);
    if (!p) return;

    if (draggingPointId) {
      const world = screenToWorld(p.x, p.y);
      const next = points.map((pt) =>
        pt.id === draggingPointId
          ? { ...pt, x: world.x, y: world.y }
          : pt
      );
      patchPoints(next);
      return;
    }

    if (isPanning && lastPointer) {
      const dx = p.x - lastPointer.x;
      const dy = p.y - lastPointer.y;
      setPan((prev) => ({
        x: prev.x + dx,
        y: prev.y + dy,
      }));
      setLastPointer(p);
    }
  }

  function handlePointerUp() {
    setDraggingPointId(null);
    setIsPanning(false);
    setLastPointer(null);
  }

  function removeSelectedPoint() {
    if (!selectedPointId) return;
    const next = points.filter((pt) => pt.id !== selectedPointId);
    patchPoints(next);
    onSelectPoint?.(null);
  }

  function moveSelectedPoint(dx, dy) {
    if (!selectedPointId) return;
    const next = points.map((pt) =>
      pt.id === selectedPointId
        ? {
            ...pt,
            x: snapValue(pt.x + dx, snapStep),
            y: snapValue(pt.y + dy, snapStep),
          }
        : pt
    );
    patchPoints(next);
  }

  function zoomBy(factor) {
    setZoom((prev) => {
      const next = prev * factor;
      return Math.max(0.1, Math.min(4, next));
    });
  }

  function fitToContent() {
    if (!points.length) {
      setZoom(0.6);
      setPan({ x: 120, y: 560 });
      return;
    }

    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const width = Math.max(maxX - minX, 100);
    const height = Math.max(maxY - minY, 100);

    const zoomX = (CANVAS_WIDTH - 160) / width;
    const zoomY = (CANVAS_HEIGHT - 160) / height;
    const nextZoom = Math.max(0.1, Math.min(4, Math.min(zoomX, zoomY)));

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    setZoom(nextZoom);
    setPan({
      x: CANVAS_WIDTH / 2 - centerX * nextZoom,
      y: CANVAS_HEIGHT / 2 + centerY * nextZoom,
    });
  }

  useEffect(() => {
    function onKeyDown(e) {
      if (!selectedPointId) return;

      const step = e.shiftKey ? snapStep * 10 : snapStep;

      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        removeSelectedPoint();
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        moveSelectedPoint(-step, 0);
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        moveSelectedPoint(step, 0);
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        moveSelectedPoint(0, step);
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        moveSelectedPoint(0, -step);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedPointId, points, snapStep]);

  const pathD = points
    .map((pt, index) => {
      const s = worldToScreen(pt);
      return `${index === 0 ? "M" : "L"} ${s.x} ${s.y}`;
    })
    .join(" ");

  const closedPathD = points.length >= 3 ? `${pathD} Z` : pathD;

  const gridLines = [];
  const visibleWorldLeft = (-pan.x) / zoom;
  const visibleWorldRight = (CANVAS_WIDTH - pan.x) / zoom;
  const visibleWorldTop = pan.y / zoom;
  const visibleWorldBottom = (pan.y - CANVAS_HEIGHT) / zoom;

  const startX = Math.floor(visibleWorldLeft / gridMinor) * gridMinor;
  const endX = Math.ceil(visibleWorldRight / gridMinor) * gridMinor;
  const startY = Math.floor(visibleWorldBottom / gridMinor) * gridMinor;
  const endY = Math.ceil(visibleWorldTop / gridMinor) * gridMinor;

  for (let x = startX; x <= endX; x += gridMinor) {
    const sx = pan.x + x * zoom;
    gridLines.push(
      <line
        key={`vx_${x}`}
        x1={sx}
        y1={0}
        x2={sx}
        y2={CANVAS_HEIGHT}
        stroke={x % gridMajor === 0 ? "#94a3b8" : "#e5e7eb"}
        strokeWidth={x % gridMajor === 0 ? 1.2 : 0.6}
      />
    );
  }

  for (let y = startY; y <= endY; y += gridMinor) {
    const sy = pan.y - y * zoom;
    gridLines.push(
      <line
        key={`hy_${y}`}
        x1={0}
        y1={sy}
        x2={CANVAS_WIDTH}
        y2={sy}
        stroke={y % gridMajor === 0 ? "#94a3b8" : "#e5e7eb"}
        strokeWidth={y % gridMajor === 0 ? 1.2 : 0.6}
      />
    );
  }

  const origin = worldToScreen({ x: 0, y: 0 });

  return (
    <div className="p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setTool("select")}
          className={`px-3 py-2 rounded border ${tool === "select" ? "bg-blue-600 text-white" : ""}`}
        >
          Select
        </button>
        <button
          onClick={() => setTool("add")}
          className={`px-3 py-2 rounded border ${tool === "add" ? "bg-blue-600 text-white" : ""}`}
        >
          Add Point
        </button>
        <button
          onClick={() => setTool("pan")}
          className={`px-3 py-2 rounded border ${tool === "pan" ? "bg-blue-600 text-white" : ""}`}
        >
          Pan
        </button>

        <div className="ml-4 flex items-center gap-2">
          <span className="text-sm">Snap</span>
          <select
            value={snapStep}
            onChange={(e) => setSnapStep(Number(e.target.value))}
            className="px-2 py-2 border rounded"
          >
            <option value={1}>1 mm</option>
            <option value={5}>5 mm</option>
            <option value={10}>10 mm</option>
            <option value={25}>25 mm</option>
            <option value={50}>50 mm</option>
          </select>
        </div>

        <div className="ml-4 flex items-center gap-2">
          <button onClick={() => zoomBy(1.2)} className="px-3 py-2 rounded border">
            Zoom +
          </button>
          <button onClick={() => zoomBy(1 / 1.2)} className="px-3 py-2 rounded border">
            Zoom -
          </button>
          <button onClick={fitToContent} className="px-3 py-2 rounded border">
            Fit
          </button>
        </div>

        <div className="ml-auto text-xs text-gray-500">
          Drag point · Arrows move selected · Shift = larger step
        </div>
      </div>

      <div className="border rounded bg-white overflow-hidden">
        <svg
          ref={svgRef}
          width="100%"
          viewBox={`0 0 ${CANVAS_WIDTH} ${CANVAS_HEIGHT}`}
          className={`w-full h-[700px] block ${tool === "add" ? "cursor-crosshair" : tool === "pan" ? "cursor-grab" : "cursor-default"}`}
          onClick={handleBackgroundClick}
          onPointerDown={handlePointerDownBackground}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        >
          {gridLines}

          <line
            x1={0}
            y1={origin.y}
            x2={CANVAS_WIDTH}
            y2={origin.y}
            stroke="#16a34a"
            strokeWidth={1.5}
          />
          <line
            x1={origin.x}
            y1={0}
            x2={origin.x}
            y2={CANVAS_HEIGHT}
            stroke="#dc2626"
            strokeWidth={1.5}
          />

          {points.length >= 2 && (
            <path
              d={closedPathD}
              fill="rgba(96,165,250,0.15)"
              stroke="#2563eb"
              strokeWidth="2"
            />
          )}

          {points.map((pt, index) => {
            const s = worldToScreen(pt);
            const isSelected = pt.id === selectedPointId;

            return (
              <g key={pt.id}>
                <circle
                  cx={s.x}
                  cy={s.y}
                  r={isSelected ? 9 : 7}
                  fill={isSelected ? "#f59e0b" : "#ef4444"}
                  stroke="white"
                  strokeWidth={2}
                  style={{ cursor: "pointer" }}
                  onPointerDown={(e) => handlePointPointerDown(e, pt.id)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectPoint?.(pt.id);
                  }}
                />
                <text
                  x={s.x + 10}
                  y={s.y - 10}
                  fontSize="12"
                  fill="#111827"
                >
                  {index + 1} ({pt.x}, {pt.y})
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => moveSelectedPoint(-snapStep, 0)}
          className="px-3 py-2 rounded border"
        >
          ←
        </button>
        <button
          onClick={() => moveSelectedPoint(snapStep, 0)}
          className="px-3 py-2 rounded border"
        >
          →
        </button>
        <button
          onClick={() => moveSelectedPoint(0, snapStep)}
          className="px-3 py-2 rounded border"
        >
          ↑
        </button>
        <button
          onClick={() => moveSelectedPoint(0, -snapStep)}
          className="px-3 py-2 rounded border"
        >
          ↓
        </button>
        <button
          onClick={removeSelectedPoint}
          className="px-3 py-2 rounded border text-red-600"
        >
          Delete Selected Point
        </button>
      </div>
    </div>
  );
}